import { useFormik } from "formik";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import loginImageAr from "../../assets/Arabicb.png";
import B12Logo from "../../assets/b12_logo.svg";
import logo from "../../assets/blue_logo.svg";
import { ReactComponent as EyeIcon } from "../../assets/eye-closed.svg";
import { ReactComponent as EyeSlashIcon } from "../../assets/eye-open.svg";
import loginImageEn from "../../assets/letthecoding-begin.png";
import StudentBG from "../../assets/studentBG.svg";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import { USER_TYPE } from "../../constants/common";
import { setUser } from "../../redux/slices/userSlice";
import authServices from "../../services/authServices";
import schoolServices from "../../services/schoolServices";
import studentServices from "../../services/studentServices";
import teacherServices from "../../services/teacherServices";
import { getLanguage } from "../../utils/getLanguage";
import { getUserRoleById } from "../../utils/getUserRoleById";
import { loginValidationSchema } from "../../utils/validationSchema";

const Login = () => {
  const { t } = useTranslation();
  const lang = getLanguage();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Formik setup for username and password
  const formik = useFormik({
    initialValues: {
      username: "",
      password: "",
    },
    validationSchema: loginValidationSchema(t),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const payload = new FormData();
        payload.append("username", values.username);
        payload.append("password", values.password);
        payload.append("type", "username");

        const response = await authServices.login(payload);

        if (response?.is_b2c) {
          const user = {
            role: "kid",
          };
          dispatch(setUser(user));
          return;
        }
        // Get the role using the user_type.id from the response
        const role = getUserRoleById(response.user_type.id);

        if (!role) {
          throw new Error("Invalid user type id received from the response");
        }
        const user = { ...response, role };
        dispatch(setUser(user));

        switch (role) {
          case USER_TYPE.SCHOOL.ROLE:
            if (response && response.access_token) {
              const schoolAdminResponse = await schoolServices.getSchoolData();

              const user = { ...response, ...schoolAdminResponse?.schools[0], role };
              dispatch(setUser(user));
            }
            break;
          case USER_TYPE.TEACHER.ROLE:
            if (response && response.access_token) {
              const teacherResponse = await teacherServices.getTeacherData();

              const user = {
                ...response,
                teacher_id: teacherResponse?.id,
                has_children_courses: teacherResponse?.has_children_courses,
                has_other_courses: teacherResponse?.has_other_courses,
                school_name: teacherResponse?.school_name,
                role,
              };
              dispatch(setUser(user));
            }
            break;
          case USER_TYPE.STUDENT.ROLE:
            if (response && response.access_token) {
              const studentResponse = await studentServices.getStudentByUserID(response?.id);

              const user = {
                ...response,
                student_id: studentResponse?.id,
                school: studentResponse?.school,
                role,
              };
              dispatch(setUser(user));
            }
            break;

          default:
            break;
        }
        setErrorMessage("");
        setTimeout(() => {
          setIsSubmitting(false);
        }, 500);
      } catch (error) {
        setIsSubmitting(false);
        setErrorMessage(error);
      }
    },
  });

  const loginWithB12 = () => {
    const authConfig = {
      domain: `${process.env.REACT_APP_B12_AUTH_URL}`,
      clientID: "helloWorldKids",
      redirectUri: `${process.env.REACT_APP_API_BASE_URL}/b12/callback`,
      responseType: "id_token token",
      scope: "default-api openid",
      nonce: "HWK",
    };

    const buildAuthUrl = ({ domain, clientID, redirectUri, responseType, scope, nonce }) => {
      const params = new URLSearchParams({
        client_id: clientID,
        redirect_uri: redirectUri,
        response_type: responseType,
        scope,
        nonce,
      });

      return `${domain}?${params.toString()}`;
    };

    const authUrl = buildAuthUrl(authConfig);
    window.location.href = authUrl;
  };

  return (
    <>
      <header className="bg-white shadow-md">
        <div className="container mx-auto flex flex-wrap items-center justify-between gap-y-4 px-5 py-4 md:flex-nowrap">
          <div onClick={() => navigate("")} className="flex cursor-pointer items-center">
            <img src={logo} alt="HelloCode" className="h-10" />
          </div>
          <LanguageSwitcher isWhite />
        </div>
      </header>
      <div className="relative flex min-h-[calc(100vh-73px)] flex-col-reverse items-center justify-center bg-[#E9F1FD] px-4 py-6 md:flex-row md:items-start md:justify-center md:gap-10">
        <div className="absolute left-0 top-0 z-0 h-[26vw] w-full bg-white" />
        <img
          src={StudentBG}
          alt="Student Background"
          className="absolute top-[10vw] w-[100vw] object-cover"
        />

        {/* Left image */}
        <div className="z-20 hidden w-full max-w-md md:block md:w-1/2 md:max-w-xl">
          <img
            src={lang === "ar" ? loginImageAr : loginImageEn}
            alt="Login visual"
            className="w-full object-contain"
          />
        </div>

        {/* Right login form */}
        <div className="relative z-20 w-full max-w-md rounded-2xl bg-blue-500 p-6 text-white shadow-2xl sm:p-8 md:w-1/3 md:p-10">
          <h1 className="mb-4 text-center text-2xl font-bold md:mb-6 md:text-3xl">{t("welcome_back")}!</h1>

          {errorMessage && (
            <p className="mb-3 rounded-xl bg-red-100 p-3 text-center text-sm text-red-500">{errorMessage}</p>
          )}

          <form onSubmit={formik.handleSubmit}>
            {/* Username */}
            <div className="mb-3">
              <label className={`text-sm ${errorMessage && "text-red-200"}`}>{t("username_or_email")}</label>
              <input
                type="text"
                name="username"
                value={formik.values.username}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`mt-1 w-full rounded-2xl bg-white px-4 py-2 text-black focus:outline-none focus:ring-2 focus:ring-blue-300 ${
                  errorMessage && "border-4 border-red-200"
                }`}
              />
            </div>

            {/* Password */}
            <div className="mb-3">
              <label className={`text-sm ${errorMessage && "text-red-200"}`}>{t("password")}</label>
              <div className="relative mt-1">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  autoComplete="new-password"
                  className={`w-full rounded-2xl bg-white px-4 py-2 text-black focus:outline-none focus:ring-2 focus:ring-blue-300 ltr:pr-10 rtl:pl-10 ${
                    errorMessage && "border-4 border-red-200"
                  }`}
                />
                <div
                  className="absolute top-1/2 -translate-y-1/2 cursor-pointer text-gray-700 ltr:right-3 rtl:left-3"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-mutedText dark:text-dark-mutedText" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-mutedText dark:text-dark-mutedText" />
                  )}
                </div>
              </div>
            </div>

            <div className="mb-3 text-center text-sm md:text-justify">
              <a href="/forgot-password" className="text-blue-200 hover:underline">
                {t("forgot_password")}
              </a>
            </div>

            {/* Submit Button */}
            <div className="my-7 flex w-full justify-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="wavy-btn relative mx-auto min-w-40 rounded-3xl border-4 border-[#96B8F3] bg-[#ffbf0f] px-4 py-2 font-semibold text-white transition duration-200 hover:bg-orange-600"
              >
                {isSubmitting ? t("signing_in") : t("login")}
              </button>
            </div>
          </form>

          <div className="my-4 flex items-center gap-2 text-white">
            <hr className="flex-1 border-blue-200" />
            <span className="text-sm text-blue-200">{t("or")}</span>
            <hr className="flex-1 border-blue-200" />
          </div>

          <button
            onClick={loginWithB12}
            className="flex w-full items-center justify-center gap-2 rounded-3xl border-4 border-blue-400 bg-white px-4 py-2 text-sm font-semibold text-black shadow-sm transition hover:bg-gray-100"
          >
            <img src={B12Logo} alt="B12" className="h-5" />
            {t("continue_with_b12")}
          </button>

          <p className="mt-6 text-center text-sm text-blue-200">
            {t("dont_have_account")}{" "}
            <a className="font-semibold text-white hover:underline">{t("register")}</a>
          </p>
        </div>
      </div>
    </>
  );
};

export default Login;
